import { CalendarToday } from '@vedmarket/ui-kit';
import { DatePicker } from '@mui/x-date-pickers-pro';
import {useFormContext, useWatch} from "react-hook-form";
import {useTranslation} from "react-i18next";
import moment, {Moment} from 'moment'
import 'moment/locale/ru';
import {useContext, useEffect, useState} from "react";
import {DELIVERY_FORM, DeliveryFormFields} from "../../index";
import {useDemoDeliveryDate} from 'components/cabinet/demo/steps/delivery/use-delivery';
import {isDemoPage} from "../../../../../../../utils/hooks";
import {DemoPlayerContext} from 'components/cabinet/demo/player-clock';

type Props = {
  demoDate?: string
  isBigInputs?: boolean
}


const DateInput = ({ demoDate, isBigInputs }: Props) => {

  const { t } = useTranslation()
  const demoPlayer = useContext(DemoPlayerContext)
  const isFullscreen = Boolean(demoPlayer?.isFullscreen)

  const { register, getValues, setValue, trigger, control } = useFormContext<DeliveryFormFields>()

  const plannedShipmentDate = useWatch({
    control,
    name: DELIVERY_FORM.PLANNED_SHIPMENT_DATE
  });

  const getDateValue = (value?: string) => {
    if (!value) {
      return null;
    }

    const formattedValue = moment(value);

    return formattedValue.isValid() ? formattedValue : null;
  }

  const initialDate = isDemoPage() && demoDate
    ? null
    : getDateValue(getValues()[DELIVERY_FORM.PLANNED_SHIPMENT_DATE])

  const [ date, setDate ] = useState<Moment | null>(initialDate);

  const [ error, setError ] = useState(false);

  const demoPlayback = useDemoDeliveryDate({ demoDate, setDate, setValue })

  const getHasDateError = (value: Moment | null) => {
    const formattedDate = moment(value).format('YYYY-MM-DD');

    return formattedDate === 'Invalid date' || Boolean(value && moment().diff(moment(value), 'days') > 0);
  }

  useEffect(() => {
    setDate(getDateValue(plannedShipmentDate));
  }, [ plannedShipmentDate ])

  const handleValidateDatePicker = async (value: Moment | null) => {
    const formattedDate = value ? moment(value).format('YYYY-MM-DD') : '';
    const hasDateError = getHasDateError(value);

    setDate(value);
    setValue(DELIVERY_FORM.PLANNED_SHIPMENT_DATE, formattedDate, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setError(hasDateError)
    await trigger(DELIVERY_FORM.PLANNED_SHIPMENT_DATE);
  }

  return (
    <div id={DELIVERY_FORM.PLANNED_SHIPMENT_DATE} style={{width: '100%'}}>
      <DatePicker
        label={t('calc__text__delivery__plannedShipmentDate')}
        {...register(DELIVERY_FORM.PLANNED_SHIPMENT_DATE, {
          required: t('common__text__isRequired'),
          validate: (value) => !getHasDateError(getDateValue(value)) || t('errors__message__incorrectDate')
        })}
        slots={{ openPickerIcon: CalendarToday }}
        value={date}
        {...demoPlayback}
        sx={{
          maxWidth: isBigInputs ? '360px' : 'auto',
          width: '100%',
          '& .MuiPickersOutlinedInput-root': {
            height: '48px',
            minHeight: '48px',
          },
          '& .MuiPickersInputBase-sectionsContainer': {
            padding: '3.5px 0',
          },
        }}
        onChange={(value) => handleValidateDatePicker(value)}
        slotProps={{
          popper: {
            placement: isFullscreen ? 'bottom-start' : 'right-start',
            modifiers: [
              { name: 'offset', options: { offset: [ 0, 8 ] } },
              { name: 'preventOverflow', options: { altAxis: true, tether: false, padding: 8 } },
              {
                name: 'flip',
                options: {
                  altAxis: false,
                  fallbackPlacements: isFullscreen ? [ 'top-start', 'right-start' ] : [ 'bottom-start', 'top-start' ],
                  padding: 8,
                },
              },
            ],
          },
          textField: {
            error,
            helperText: error ? t('errors__message__incorrectDate') : '',
            InputLabelProps: { shrink: true },
          },
        }}
      />
    </div>

  )
}

export default DateInput
